import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Edit3, Plus, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getLabServices, updateServiceStatus, updateServiceFeature } from "../../apiCalls/services";
import { toast } from "react-toastify";

const ServiceList = ({ labId }) => {
  const dispatch  = useDispatch();
  const { services } = useSelector((state) => state.service);

  const [search,   setSearch]   = useState("");
  // tracks which service ids are mid-request: { [id]: Set<'status'|'featured'> }
  const [loading,  setLoading]  = useState({});

  useEffect(() => {
    getLabServices(dispatch, labId);
  }, [dispatch, labId]);

  /* ── derived data ── */
  const displayed = useMemo(() => {
    if (!services?.length) return [];
    if (!search.trim()) return services;
    const q = search.toLowerCase();
    return services.filter(
      (s) =>
        s?.testName?.subCategoryName?.toLowerCase().includes(q) ||
        s?.testType?.categoryName?.toLowerCase().includes(q)
    );
  }, [services, search]);

  const totalServices = services?.length ?? 0;
  const totalActive   = services?.filter((s) => s.status).length   ?? 0;
  const totalFeatured = services?.filter((s) => s.featured).length ?? 0;
  const avgCharge     = totalServices
    ? Math.round(services.reduce((s, x) => s + Number(x.charges || 0), 0) / totalServices)
    : 0;

  /* ── helpers ── */
  const setFieldLoading = (id, field, val) =>
    setLoading((prev) => {
      const next = { ...prev };
      if (val) {
        next[id] = new Set([...(next[id] || []), field]);
      } else {
        next[id]?.delete(field);
        if (!next[id]?.size) delete next[id];
      }
      return next;
    });

  const isLoading = (id, field) => !!loading[id]?.has(field);

  const onSuccess = useCallback(
    (field) => (res, ok) => {
      if (ok) {
        toast.success(`Service ${field} updated`);
        getLabServices(dispatch, labId);
      } else {
        toast.error("Update failed. Try again.");
      }
    },
    [dispatch, labId]
  );

  /* ── dedicated toggle handlers ── */
  const handleStatusToggle = useCallback(
    async (service) => {
      const { _id, status } = service;
      setFieldLoading(_id, "status", true);
      await updateServiceStatus(dispatch, _id, { status: !status }, onSuccess("status"));
      setFieldLoading(_id, "status", false);
    },
    [dispatch, onSuccess]
  );

  const handleFeaturedToggle = useCallback(
    async (service) => {
      const { _id, featured } = service;
      setFieldLoading(_id, "featured", true);
      await updateServiceFeature(dispatch, _id, { featured: !featured }, onSuccess("featured"));
      setFieldLoading(_id, "featured", false);
    },
    [dispatch, onSuccess]
  );

  /* ── render ── */
  return (
    <div className="dt-page">

      {/* Header */}
      <div className="dt-header">
        <div>
          <h4 className="dt-title">Services</h4>
          <p className="dt-subtitle" style={{ color: "var(--gray-400)", fontSize: "0.8rem" }}>
            {displayed.length} service{displayed.length !== 1 ? "s" : ""} listed
          </p>
        </div>
        <Link to={`/profile/${labId}/add-service`} className="app-btn app-btn--primary">
          <Plus size={15} /> Add Service
        </Link>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "1rem" }}>
        {[
          { label: "Total",     value: totalServices, icon: "bi-list-ul",         color: "#8166f4", bg: "#f3f0ff" },
          { label: "Active",    value: totalActive,   icon: "bi-check-circle",    color: "#10b981", bg: "#d1fae5" },
          { label: "Featured",  value: totalFeatured, icon: "bi-star-fill",       color: "#f59e0b", bg: "#fef3c7" },
          { label: "Avg Charge",value: `₹${avgCharge}`,icon: "bi-currency-rupee", color: "#3b82f6", bg: "#dbeafe" },
        ].map((s) => (
          <div key={s.label} style={{
            background: "#fff", border: "1px solid var(--gray-100)",
            borderRadius: "var(--radius-lg)", padding: "1rem 1.1rem",
            display: "flex", alignItems: "center", gap: "0.75rem",
            boxShadow: "0 2px 10px rgba(129,102,244,0.05)",
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: "var(--radius-md)",
              background: s.bg, display: "flex", alignItems: "center",
              justifyContent: "center", flexShrink: 0,
            }}>
              <i className={`bi ${s.icon}`} style={{ fontSize: "1rem", color: s.color }} />
            </div>
            <div>
              <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--gray-900)", lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: "0.7rem", color: "var(--gray-500)", marginTop: 3 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search toolbar */}
      <div className="dt-toolbar">
        <div className="dt-search-wrap">
          <i className="bi bi-search dt-search-icon" />
          <input
            type="text"
            className="dt-search"
            placeholder="Search by service or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="dt-search-clear" onClick={() => setSearch("")}>
              <i className="bi bi-x" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="dt-wrap">
        {displayed.length === 0 ? (
          <div className="dt-empty">
            <i className="bi bi-flask dt-empty-icon" />
            <p style={{ color: "var(--gray-400)", fontSize: "0.9rem" }}>
              {search ? "No services match your search" : "No services added yet"}
            </p>
            {!search && (
              <Link to={`/profile/${labId}/add-service`} className="app-btn app-btn--primary app-btn--sm">
                <Plus size={13} /> Add First Service
              </Link>
            )}
          </div>
        ) : (
          <table className="dt-table">
            <thead>
              <tr>
                <th style={{ width: "3rem" }}>#</th>
                <th>Service Name</th>
                <th>Category</th>
                <th>Duration</th>
                <th>Charges</th>
                <th>Status</th>
                <th>Featured</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((data, index) => (
                <tr key={data._id || index}>

                  {/* # */}
                  <td className="dt-cell-num">{index + 1}</td>

                  {/* Service name */}
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: "var(--radius-md)", flexShrink: 0,
                        background: data?.featured ? "#fef3c7" : "var(--primary-light)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {data?.featured
                          ? <Star size={14} style={{ color: "#f59e0b", fill: "#f59e0b" }} />
                          : <i className="bi bi-capsule" style={{ fontSize: "0.85rem", color: "var(--primary)" }} />}
                      </div>
                      <div>
                        <span style={{ fontWeight: 600, color: "var(--gray-800)", display: "block" }}>
                          {data?.testName?.subCategoryName || "—"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td>
                    <span style={{
                      padding: "0.2rem 0.65rem", borderRadius: "9999px",
                      fontSize: "0.72rem", fontWeight: 600,
                      background: "var(--info-light)", color: "#1e40af",
                    }}>
                      {data?.testType?.categoryName || "—"}
                    </span>
                  </td>

                  {/* Duration */}
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", color: "var(--gray-500)", fontSize: "0.84rem" }}>
                      <i className="bi bi-clock" style={{ fontSize: "0.72rem" }} />
                      {data?.duration} min
                    </span>
                  </td>

                  {/* Charges */}
                  <td>
                    <span style={{ fontWeight: 700, color: "var(--gray-800)" }}>₹{data?.charges}</span>
                  </td>

                  {/* Status toggle — uses updateServiceStatus */}
                  <td>
                    <button
                      onClick={() => handleStatusToggle(data)}
                      disabled={isLoading(data._id, "status")}
                      title={data?.status ? "Deactivate service" : "Activate service"}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "0.3rem",
                        padding: "0.28rem 0.65rem", borderRadius: "9999px",
                        fontSize: "0.7rem", fontWeight: 600,
                        border: "none", cursor: isLoading(data._id, "status") ? "not-allowed" : "pointer",
                        opacity: isLoading(data._id, "status") ? 0.6 : 1,
                        transition: "all 0.15s",
                        background: data?.status ? "var(--success-light)" : "var(--danger-light)",
                        color: data?.status ? "#065f46" : "#991b1b",
                      }}
                    >
                      {isLoading(data._id, "status")
                        ? <span className="svc-spinner" style={{ width: 10, height: 10 }} />
                        : <i className={`bi bi-${data?.status ? "check-circle" : "x-circle"}`} style={{ fontSize: "0.7rem" }} />
                      }
                      {data?.status ? "Active" : "Inactive"}
                    </button>
                  </td>

                  {/* Featured toggle — uses updateServiceFeature */}
                  <td>
                    <button
                      onClick={() => handleFeaturedToggle(data)}
                      disabled={isLoading(data._id, "featured")}
                      title={data?.featured ? "Remove from featured" : "Mark as featured"}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "0.3rem",
                        padding: "0.28rem 0.65rem", borderRadius: "9999px",
                        fontSize: "0.7rem", fontWeight: 600,
                        border: "none", cursor: isLoading(data._id, "featured") ? "not-allowed" : "pointer",
                        opacity: isLoading(data._id, "featured") ? 0.6 : 1,
                        transition: "all 0.15s",
                        background: data?.featured ? "#fef3c7" : "var(--gray-100)",
                        color: data?.featured ? "#92400e" : "var(--gray-500)",
                      }}
                    >
                      {isLoading(data._id, "featured")
                        ? <span className="svc-spinner" style={{ width: 10, height: 10 }} />
                        : <Star size={11} style={{ fill: data?.featured ? "#f59e0b" : "none", color: data?.featured ? "#f59e0b" : "var(--gray-400)" }} />
                      }
                      {data?.featured ? "Featured" : "Not Featured"}
                    </button>
                  </td>

                  {/* Edit */}
                  <td>
                    <Link
                      to={`/profile/${labId}/update-service/${data?._id}`}
                      className="app-btn app-btn--outline-primary app-btn--sm"
                    >
                      <Edit3 size={13} /> Edit
                    </Link>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ServiceList;
